import { processDownload } from '@pba/react-utils';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { Button } from '../button';
import { Flex } from '../flex';
import { Modal } from '../modal';
import { Result } from '../result';
import { Text } from '../typography';
import { ErrorDetailsContent } from './error-details-content';
import { getErrorDetails } from './get-error-details';
import type { AdditionalData } from './types';

export type ErrorDebugResultOrientation = 'horizontal' | 'vertical';

export type ErrorDebugResultProperties = {
  additionalData?: AdditionalData[];
  error: unknown;
  message?: string;
  orientation?: ErrorDebugResultOrientation;
  size?: ErrorDebugResultSize;
};

export type ErrorDebugResultSize = 'large' | 'small';

const ErrorDebugResult = ({
  additionalData,
  error,
  message,
  orientation = 'vertical',
  size = 'small',
}: ErrorDebugResultProperties) => {
  const [modal, contextHolder] = Modal.useModal();

  const showDetails = useCallback(() => {
    const details = getErrorDetails(error, additionalData);

    modal.confirm({
      content: <ErrorDetailsContent details={details} />,
      icon: null,
      okText: 'Download',
      onOk() {
        processDownload({
          data: JSON.stringify(details, null, 2),
          // TODO: Fix
          fileName: `error-${format(details.timestamp, 'YYYY-MM-DD_HH-mm-ss')}.json`,
        });
      },
      title: 'Error details',
      width: 1200,
    });
  }, [additionalData, error, modal]);

  if (size === 'large') {
    return (
      <>
        {contextHolder}
        <Result
          extra={
            <Button onClick={showDetails} size="large" type="primary">
              Show details
            </Button>
          }
          status="500"
          subTitle={message ?? 'Something went wrong'}
        />
      </>
    );
  }

  const isVertical = orientation === 'vertical';

  const text = <Text>{message ?? 'Sorry, something went wrong.'}</Text>;

  return (
    <Flex align="center" gap="xs" justify="center" vertical={isVertical}>
      {contextHolder}
      {isVertical ? text : null}
      <Button onClick={showDetails} size="small" type="primary">
        Show details
      </Button>
      {isVertical ? null : text}
    </Flex>
  );
};

export default ErrorDebugResult;
